import axios from 'axios';

/**
 * Generate a consistent session key for a given video and language combination
 * Can be optionally extended with a section ID for section-specific sessions
 *
 * @param {string} videoId - The ID of the video
 * @param {string} languageName - The language name
 * @param {string} [sectionId] - Optional section ID for section editing sessions
 * @returns {string} A consistent session key
 */
export const generateSessionKey = (videoId, languageName, sectionId = null) => {
    if (sectionId) {
        return `edit_session_${videoId}_${languageName}_section_${sectionId}`;
    }
    return `edit_session_${videoId}_${languageName}`;
};

/**
 * Generate a similar key pattern for read-only status
 *
 * @param {string} videoId - The ID of the video
 * @param {string} languageName - The language name
 * @param {string} [sectionId] - Optional section ID for section editing sessions
 * @returns {string} A consistent read-only key
 */
export const generateReadOnlyKey = (videoId, languageName, sectionId = null) => {
    if (sectionId) {
        return `read_only_${videoId}_${languageName}_section_${sectionId}`;
    }
    return `read_only_${videoId}_${languageName}`;
};

class EditSessionService {
    generateSessionId() {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    }

    /**
     * Start an edit session for a video language
     */
    async startEditSession(videoId, languageName, email, authToken, editorVersion) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 10000;

        // Get or create session ID once — shared across all attempts
        const sessionKey = generateSessionKey(videoId, languageName);
        let sessionId = sessionStorage.getItem(sessionKey);
        if (!sessionId) {
            sessionId = this.generateSessionId();
            console.log(`Starting new edit session for ${sessionKey}: ${sessionId}`);
            sessionStorage.setItem(sessionKey, sessionId);
        }

        let lastError;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                const response = await axios.post(
                    `${
                        process.env.REACT_APP_API_URL || ''
                    }/api/v3/videos/${videoId}/languages/${languageName}/edit-session/start`,
                    {},
                    {
                        headers: {
                            email: email,
                            auth: authToken,
                        },
                        params: {
                            sessionId: sessionId,
                            editorVersion: editorVersion,
                        },
                    },
                );

                // Handle different status responses
                if (response.status === 200) {
                    if (response.data.sessionId) {
                        // Store the session ID returned by the server
                        console.log(`Edit session started successfully for ${sessionKey}: ${response.data.sessionId}`);
                        sessionStorage.setItem(sessionKey, response.data.sessionId);
                    }
                }

                return response.data;
            } catch (error) {
                // 409 Conflict = legitimate duplicate session — do not retry
                if (error.response && error.response.status === 409) {
                    if (error.response.data.sessionId) {
                        console.warn(`Duplicate session detected for ${sessionKey}: ${error.response.data.sessionId}`);
                        sessionStorage.setItem(sessionKey, error.response.data.sessionId);
                    }
                    return error.response.data;
                }

                lastError = error;
                if (attempt < MAX_RETRIES) {
                    console.warn(
                        `Edit session start failed (attempt ${attempt}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS / 1000}s...`,
                        error.message,
                    );
                    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
                } else {
                    console.error(`Edit session start failed after ${MAX_RETRIES} attempts:`, error);
                }
            }
        }

        throw lastError;
    }

    /**
     * Keep the edit session alive with periodic updates
     */
    async keepAliveEditSession(videoId, languageName, email, authToken) {
        try {
            const sessionKey = generateSessionKey(videoId, languageName);
            const sessionId = sessionStorage.getItem(sessionKey);

            if (!sessionId) {
                console.log(`No session ID found for keep-alive request  ${sessionKey}`);
                return { status: 'NO_SESSION' };
            }

            const response = await axios.put(
                `${
                    process.env.REACT_APP_API_URL || ''
                }/api/v3/videos/${videoId}/languages/${languageName}/edit-session/keep-alive`,
                {},
                {
                    headers: {
                        email: email,
                        auth: authToken,
                    },
                    params: {
                        sessionId: sessionId,
                    },
                },
            );
            return response.data;
        } catch (error) {
            console.error('Error keeping edit session alive:', error);
            throw error;
        }
    }

    /**
     * End an edit session
     */
    async endEditSession(videoId, languageName, email, authToken) {
        const sessionKey = generateSessionKey(videoId, languageName);
        const sessionId = sessionStorage.getItem(sessionKey);

        // Always clean up the local session storage regardless of API call result
        console.log(`Ending edit session for ${sessionKey}: ${sessionId}`);
        sessionStorage.removeItem(sessionKey);

        // If no session ID, just return early
        if (!sessionId) {
            console.warn(`No session ID found for end session request ${sessionKey}`);
            return { status: 'NO_SESSION' };
        }

        try {
            const response = await axios.post(
                `${
                    process.env.REACT_APP_API_URL || ''
                }/api/v3/videos/${videoId}/languages/${languageName}/edit-session/end`,
                {},
                {
                    headers: {
                        email: email,
                        auth: authToken,
                    },
                    params: {
                        sessionId: sessionId,
                    },
                },
            );

            return response.data;
        } catch (error) {
            console.error('Error ending edit session:', error);
            // Even though the API call failed, we've already cleaned up the local session
            return { status: 'CLEANUP_ONLY' };
        }
    }

    /**
     * Clean up a local session without calling the API
     */
    cleanupLocalSession(videoId, languageName) {
        const sessionKey = generateSessionKey(videoId, languageName);
        console.log(`Cleaning up local session for ${sessionKey}`);
        sessionStorage.removeItem(sessionKey);
        return { status: 'CLEANUP_ONLY' };
    }

    /**
     * Mark a session as read-only (for duplicate sessions)
     * @param {string} videoId - The video ID
     * @param {string} languageName - The language name
     * @param {string} sessionId - The session ID
     */
    markSessionAsReadOnly(videoId, languageName, sessionId) {
        const readOnlyKey = `read_only_${videoId}_${languageName}`;
        sessionStorage.setItem(readOnlyKey, 'true');
    }

    /**
     * Check if current session is read-only
     * @param {string} videoId - The video ID
     * @param {string} languageName - The language name
     * @returns {boolean} - Whether this is a read-only session
     */
    isReadOnlySession(videoId, languageName) {
        const readOnlyKey = `read_only_${videoId}_${languageName}`;
        return sessionStorage.getItem(readOnlyKey) === 'true';
    }

    /**
     * Check if there's already a session for this video/language in another tab
     * @param {string} videoId - The video ID
     * @param {string} languageName - The language name
     * @returns {Promise<Object>} - Object with exists and sessionId properties
     */
    async checkForExistingSession(videoId, languageName) {
        return new Promise((resolve) => {
            // Generate a unique key for the channel based on video ID and language
            const channel = new BroadcastChannel(`amrutvaani-session-check-${videoId}-${languageName}`);

            // Set up listener for responses
            channel.onmessage = (event) => {
                if (event.data.hasSession) {
                    resolve({ exists: true, sessionId: event.data.sessionId });
                    channel.close();
                }
            };

            // Send a message to check for existing sessions
            channel.postMessage({ checkSession: true });

            // Set a timeout to resolve if no responses are received
            setTimeout(() => {
                resolve({ exists: false });
                channel.close();
            }, 500); // Give 500ms for other tabs to respond
        });
    }

    // End edit session using beacon API for page unload
    endEditSessionWithBeacon(videoId, language) {
        try {
            const sessionKey = generateSessionKey(videoId, language);
            const sessionId = sessionStorage.getItem(sessionKey);

            if (!sessionId) {
                console.warn('No session ID found for endEditSessionWithBeacon');
                return false;
            }

            // Get auth data from localStorage
            const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
            const authToken = JSON.parse(localStorage.getItem('authToken') || '""');

            // Create a FormData object for the beacon payload
            const data = new FormData();
            data.append('sessionId', sessionId);
            data.append('email', authUser.email);
            data.append('auth', authToken);

            // SendBeacon only accepts two arguments: URL and data
            const result = navigator.sendBeacon(
                `${
                    process.env.REACT_APP_API_URL || ''
                }/api/v3/videos/${videoId}/languages/${language}/edit-session/end-beacon`,
                data,
            );

            return result;
        } catch (error) {
            console.error('Error with sendBeacon for ending session:', error);
            // Still clean up the session storage
            const sessionKey = generateSessionKey(videoId, language);
            sessionStorage.removeItem(sessionKey);
            return false;
        }
    }

    // ── Text-file session helpers ──────────────────────────────────────────────

    generateTextFileSessionKey(fileId, languageName) {
        return `textfile_edit_session_${fileId}_${languageName}`;
    }

    async startTextFileEditSession(fileId, languageName, email, authToken) {
        const sessionKey = this.generateTextFileSessionKey(fileId, languageName);
        let sessionId = sessionStorage.getItem(sessionKey);
        if (!sessionId) {
            sessionId = this.generateSessionId();
            sessionStorage.setItem(sessionKey, sessionId);
        }
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL || ''}/api/v2/textfiles/${fileId}/languages/${languageName}/edit-session/start`,
                {},
                { headers: { email, auth: authToken }, params: { sessionId } },
            );
            if (response.data.sessionId) {
                sessionStorage.setItem(sessionKey, response.data.sessionId);
            }
            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 409) {
                // Do NOT overwrite sessionStorage with the conflicting tab's sessionId.
                // If we did, the next call to start() would send that same id, the
                // backend would see a match and return RECONNECTED, silently clearing
                // the read-only lock on this tab.
                console.warn(`Text file session conflict for ${this.generateTextFileSessionKey(fileId, languageName)}: ${error.response.data?.status}`);
                return error.response.data;
            }
            console.error('Error starting text file edit session:', error);
            throw error;
        }
    }

    async keepAliveTextFileEditSession(fileId, languageName, email, authToken) {
        const sessionKey = this.generateTextFileSessionKey(fileId, languageName);
        const sessionId = sessionStorage.getItem(sessionKey);
        if (!sessionId) return { status: 'NO_SESSION' };
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL || ''}/api/v2/textfiles/${fileId}/languages/${languageName}/edit-session/keep-alive`,
                {},
                { headers: { email, auth: authToken }, params: { sessionId } },
            );
            return response.data;
        } catch (error) {
            console.error('Error keeping text file edit session alive:', error);
            throw error;
        }
    }

    async endTextFileEditSession(fileId, languageName, email, authToken) {
        const sessionKey = this.generateTextFileSessionKey(fileId, languageName);
        const sessionId = sessionStorage.getItem(sessionKey);
        sessionStorage.removeItem(sessionKey);
        if (!sessionId) return { status: 'NO_SESSION' };
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL || ''}/api/v2/textfiles/${fileId}/languages/${languageName}/edit-session/end`,
                {},
                { headers: { email, auth: authToken }, params: { sessionId } },
            );
            return response.data;
        } catch (error) {
            console.error('Error ending text file edit session:', error);
            return { status: 'CLEANUP_ONLY' };
        }
    }

    endTextFileEditSessionWithBeacon(fileId, languageName) {
        try {
            const sessionKey = this.generateTextFileSessionKey(fileId, languageName);
            const sessionId = sessionStorage.getItem(sessionKey);
            if (!sessionId) return false;
            sessionStorage.removeItem(sessionKey);

            const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
            const authToken = JSON.parse(localStorage.getItem('authToken') || '""');
            const data = new FormData();
            data.append('sessionId', sessionId);
            data.append('email', authUser.email || '');
            data.append('auth', authToken);

            return navigator.sendBeacon(
                `${process.env.REACT_APP_API_URL || ''}/api/v2/textfiles/${fileId}/languages/${languageName}/edit-session/end-beacon`,
                data,
            );
        } catch (error) {
            console.error('Error with beacon for ending text file session:', error);
            return false;
        }
    }

    isReadOnlyTextFileSession(fileId, languageName) {
        return sessionStorage.getItem(`textfile_read_only_${fileId}_${languageName}`) === 'true';
    }
}

export default new EditSessionService();
